import type { JSX, RefObject } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Entity } from '../api.js';
import { t } from '../i18n.js';
import { formatDate, formatSize } from '../format.js';
import { closeViewer, viewer, viewerList } from '../state.js';
import { dismissOverlay } from '../navigation.js';
import { urlFor } from '../thumbs.js';
import { IconButton } from './parts.js';
import { ObjectMenu } from './menu.js';
import {
  IconClose,
  IconFullscreen,
  IconFullscreenExit,
  IconNext,
  IconOriginal,
  IconPrev,
  IconScreen,
} from './icons.js';
import { useDialog } from './dialog.js';

/**
 * **La visionneuse** — le trou le plus visible de l'étape 3 : une grille de
 * vignettes de 160 px qu'aucun geste n'agrandissait.
 *
 * Elle affiche le rôle `standard`, pas la source : c'est exactement ce pour quoi
 * le worker le produit — une image d'écran, quelques centaines de kilo-octets,
 * là où la source peut peser vingt mégaoctets. La source reste accessible au
 * téléchargement depuis l'inspecteur.
 *
 * **Le clavier est un citoyen de première classe** : `←`, `→`, `Échap`. Une
 * visionneuse qu'on ne peut parcourir qu'à la souris est une visionneuse qu'on
 * ne parcourt pas.
 *
 * ### L'image occupe l'écran, comme la vidéo *(2026-08-14)*
 *
 * La barre du haut et la ligne de méta étaient deux **rangées de la colonne** :
 * elles prenaient donc leur hauteur *à l'image*, qui ne recevait que ce qui
 * restait. Sur un grand écran on ne le remarquait pas ; en portrait sur un
 * téléphone, où la hauteur est la ressource rare, la photo perdait cent pixels en
 * haut et cinquante en bas — et le contraste avec la vidéo, dont le lecteur natif
 * passe en plein écran, rendait le défaut évident : « la bascule en mode portrait
 * réduit la taille de la photo avec un bandeau haut qui est conservé ».
 *
 * Les deux sont désormais **posées sur** la scène, en surimpression, sur un
 * dégradé qui les détache sans masquer l'image. La scène occupe tout l'écran, et
 * l'image tout ce que sa forme lui permet d'en prendre.
 *
 * **Un appui sur l'image efface la surimpression**, et c'est le geste de toutes
 * les visionneuses : à un moment on ne veut plus que la photo. Il est distingué
 * du balayage — un glissement ne bascule rien, faute de quoi changer d'image
 * masquerait les commandes une fois sur deux.
 *
 * ### Le pincement et le plein écran *(2026-08-15)*
 *
 * Une photo qu'on ne peut pas agrandir est une photo qu'on ne peut pas *regarder* :
 * la question qu'on pose à une visionneuse est souvent un détail — un visage au
 * fond, un panneau, une date sur un mur. Le pincement est le seul geste que tout
 * le monde essaie, et il n'existait pas.
 *
 * Il **partage ses pointeurs avec le balayage**, et c'est la seule difficulté :
 * les deux gestes commencent par un doigt posé sur la même scène. Ils sont donc
 * tenus par un gestionnaire unique, qui les départage sur deux faits observables
 * plutôt que sur une devinette — **le nombre de doigts** (deux, c'est un
 * pincement) et **l'échelle courante** (agrandie, un doigt déplace l'image ; à
 * l'échelle 1, il tourne la page). C'est la règle de toutes les galeries, et elle
 * n'a pas d'exception à écrire.
 */
export function Viewer(): JSX.Element | null {
  const index = viewer.value;
  const list = viewerList.value;
  /** La surimpression : visible d'abord, effaçable au doigt. */
  const [chrome, setChrome] = useState(true);
  const entity = index === undefined ? undefined : list[index];

  // Le dépilement passe par l'historique quand c'est la visionneuse qui y est
  // inscrite : la croix, `Échap` et le geste système font alors la même chose.
  const dismiss = (): void => dismissOverlay('viewer', closeViewer);

  // `Échap`, la mise au focus, le piège de tabulation et le verrou de défilement
  // sont communs aux quatre surcouches — le défilement de la page **sous** la
  // visionneuse est un défaut classique : on ferme, et on a perdu sa place dans
  // la grille. Les flèches, elles, n'appartiennent qu'ici.
  const host = useDialog(index !== undefined, { onEscape: dismiss, lockScroll: true });
  // **Avant les sorties anticipées** : l'ordre des crochets doit être le même à
  // chaque rendu, y compris quand la visionneuse est fermée.
  //
  // La vidéo n'est pas pinçable : son lecteur natif porte ses propres gestes — la
  // barre de lecture se traîne au doigt — et son plein écran est déjà là.
  const stage = useStage({ swipe: list.length > 1, zoom: entity?.Kind !== 'VID' });
  const full = useFullscreen(host);

  useEffect(() => {
    if (index === undefined) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'ArrowLeft') step(-1);
      if (ev.key === 'ArrowRight') step(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index]);

  // Changer d'image **ramène les commandes** — elles portent le nom et le rang,
  // qui sont précisément ce qu'on veut savoir de l'image qui vient d'arriver — et
  // **remet l'agrandissement à plat** : un facteur trois hérité de la photo
  // précédente ferait arriver la suivante par un coin, sans qu'on sache lequel.
  useEffect(() => {
    setChrome(true);
    stage.reset();
  }, [index]);

  // Fermer la visionneuse quitte le plein écran : le laisser derrière soi rendrait
  // une grille sans barre d'adresse ni barre système, sans rien pour en sortir.
  useEffect(() => {
    if (index === undefined) full.leave();
  }, [index]);

  if (index === undefined || !entity) return null;

  return (
    <div
      class={chrome ? 'viewer' : 'viewer bare'}
      ref={host}
      tabIndex={-1}
      data-testid="viewer"
      role="dialog"
      aria-modal="true"
      aria-label={entity.Name}
    >
      {/* **La scène occupe tout**, et porte la surimpression plutôt que de la
          côtoyer. `touch-action: none` est indispensable aux deux gestes — sans
          lui le navigateur prend le glissement horizontal et le pincement pour
          les siens, et émet un `pointercancel`. */}
      <div
        class="viewer-stage"
        {...stage.handlers}
        onClick={(ev) => {
          // Un balayage — et désormais un pincement — se termine par un `click`
          // que le navigateur émet quand même : sans ce garde, changer d'image ou
          // agrandir effacerait les commandes.
          if (stage.moved.current) {
            stage.moved.current = false;
            return;
          }
          // **La vidéo garde ses propres gestes.** Le lecteur natif porte ses
          // commandes *dans* l'élément : un appui sur « lire » remonterait ici et
          // effacerait la barre au moment précis où l'on démarre la lecture.
          if ((ev.target as HTMLElement | null)?.tagName === 'VIDEO') return;
          setChrome((on) => !on);
        }}
      >
        {/* **L'agrandissement porte sur une enveloppe, pas sur l'image.** La
            transformation vaut alors pour tout ce que la scène montre — le message
            d'indisponibilité comme la photo —, et le `style` reste un **objet** :
            la CSP est `style-src 'self'`, qui refuse l'attribut `style` mais pas
            le CSSOM par lequel Preact écrit un objet (voir `gallery.tsx`). */}
        <div class="viewer-zoom" data-testid="viewer-zoom" style={stage.style}>
          <Frame entity={entity} />
        </div>
      </div>

      <div class="viewer-bar">
        <IconButton testId="viewer-close" label={t('viewer.close')} onClick={dismiss}>
          <IconClose />
        </IconButton>
        <span class="viewer-title" data-testid="viewer-title">
          {entity.Name}
        </span>
        <span class="viewer-count">
          {index + 1} / {list.length}
        </span>
        {/* **Le plein écran**, quand le navigateur en a un. La visionneuse couvre
            déjà la fenêtre ; ce qu'elle ne peut pas reprendre sans cette API, ce
            sont les barres du navigateur et du système — c'est-à-dire, sur un
            téléphone, le quart de la hauteur. Le bouton disparaît là où l'API
            n'existe pas (Safari sur iPhone) plutôt que d'échouer en silence. */}
        {full.supported ? (
          <IconButton
            testId="viewer-fullscreen"
            label={t(full.on ? 'viewer.exitFullscreen' : 'viewer.fullscreen')}
            onClick={full.toggle}
          >
            {full.on ? <IconFullscreenExit /> : <IconFullscreen />}
          </IconButton>
        ) : null}
        {/* **Le même menu d'objet qu'en grille** *(2026-08-14)*. Une photo ouverte
            n'offrait aucune action : ni détails, ni collection, ni suppression.
            Il fallait refermer, retrouver la tuile, viser son « … » — et depuis
            que l'ouverture ne pose plus la fiche par-dessus l'image, c'est **ici**
            que « Détails » se demande. */}
        {entity.Fid ? <ObjectMenu entity={entity} id={entity.Fid} opened /> : null}
      </div>

      {list.length > 1 ? (
        <>
          {/* **Les flèches sont posées sur l'image, pas à côté d'elle.** Elles
              étaient deux enfants d'une rangée `flex` : sur une image large elles
              se retrouvaient *hors* de l'image, aux bords de l'écran — c'est-à-dire
              là où l'on ne regarde pas, et sur un téléphone contre les gestes
              système du bord. */}
          <span class="viewer-nav prev">
            <IconButton testId="viewer-prev" label={t('viewer.previous')} onClick={() => step(-1)}>
              <IconPrev />
            </IconButton>
          </span>
          <span class="viewer-nav next">
            <IconButton testId="viewer-next" label={t('viewer.next')} onClick={() => step(1)}>
              <IconNext />
            </IconButton>
          </span>
        </>
      ) : null}

      <p class="viewer-meta">
        {/* **`UpdatedAt` n'est jamais affiché** (règle E7) : c'est un filigrane
            de synchronisation qu'une réaction fait avancer. La date d'une photo
            est sa date de prise de vue. */}
        {formatDate(entity.DocumentDate ?? entity.CreatedAt)}
        {entity.Camera ? ` · ${entity.Camera}` : ''}
        <Quality entity={entity} />
      </p>
    </div>
  );
}

/**
 * **Le balayage** — ce qui manquait pour que la visionneuse existe sur un
 * téléphone.
 *
 * `←` et `→` en font une visionneuse au clavier ; les deux boutons en font une
 * visionneuse à la souris. Sur un écran tactile il restait à viser une cible de
 * 40 px posée sur le bord de l'image, ce que personne ne fait : on glisse. Le
 * geste manquant ne coûtait pas une fonction, il coûtait **l'usage** de l'écran.
 *
 * Les seuils ne sont pas décoratifs : un glissement horizontal de moins de 40 px
 * est un tremblement, et un glissement plus vertical qu'horizontal est un
 * défilement — les confondre ferait changer d'image quand on veut fermer.
 *
 * Le suivi passe par les événements *pointer* et non *touch* : le même code sert
 * alors au stylet et à la souris, et il n'y a qu'un chemin à tenir.
 */
const SWIPE_MIN = 40;

/** Au-delà, on ne regarde plus une photo : on regarde ses pixels. */
const MAX_SCALE = 5;

/**
 * En deçà de ce facteur, le pincement **retombe d'aplomb** au relâchement. Sans
 * ce cran, une image « presque » à l'échelle 1 reste décalée de quelques pixels
 * pour toujours, et rien dans l'écran ne dit pourquoi elle n'est pas centrée.
 */
const SNAP_SCALE = 1.05;

interface Stage {
  /** Le `style` de l'enveloppe — un **objet**, donc écrit par le CSSOM (CSP). */
  readonly style: { readonly transform?: string };
  readonly moved: { current: boolean };
  readonly handlers: Record<string, (ev: never) => void>;
  readonly reset: () => void;
}

/**
 * **Un seul gestionnaire pour le balayage et le pincement**, parce qu'ils
 * partagent leurs pointeurs.
 *
 * L'origine du geste vit dans une **référence** et non dans une variable de
 * rendu : la moindre remontée de signal entre l'appui et le relâchement
 * réinitialiserait la seconde, et le geste se perdrait sans rien dire.
 *
 * ### Deux choses faisaient échouer le balayage en silence *(corrigées le 2026-08-09)*
 *
 * ① **`touch-action`.** Tant que la scène le laisse à `auto`, le navigateur
 *    considère qu'un glissement horizontal *lui* appartient — il prend le geste
 *    et émet un `pointercancel`. Le code était juste et ne voyait jamais le
 *    `pointerup` : le retour d'usage disait « on ne peut pas slider », et rien
 *    dans la page ne le contredisait. La règle vit dans la CSS, et elle **fait
 *    partie** de ce gestionnaire — l'un ne fonctionne pas sans l'autre. Elle vaut
 *    désormais `none` et non `pan-y` : le pincement du navigateur est un geste à
 *    lui, il agrandit *la page* et non l'image, et il faut le lui reprendre pour
 *    que les deux doigts arrivent jusqu'ici.
 * ② **La capture du pointeur.** Sans elle, un doigt qui quitte la scène en cours
 *    de route — vers la barre du haut, vers le bord de l'écran — relâche
 *    ailleurs, et le geste se perd. `setPointerCapture` garantit que le
 *    `pointerup` revient ici, quel que soit l'endroit où le doigt se lève.
 *
 * ### Le partage des deux gestes *(2026-08-15)*
 *
 * Rien n'est deviné : **deux doigts** est un pincement, **un doigt sur une image
 * agrandie** la déplace, **un doigt à l'échelle 1** tourne la page. Un pincement
 * n'ouvre donc jamais l'image suivante — le drapeau `multi` tient toute la durée
 * du geste, y compris après qu'un des deux doigts s'est levé, sans quoi la fin
 * d'un pincement se lirait comme un balayage.
 *
 * Le point pincé **reste sous les doigts** : c'est ce que dit la formule
 * `t' = r·t + o(1−r) + Δc` — l'agrandissement se fait autour du milieu des deux
 * doigts, pas autour du centre de l'écran. Agrandir autour du centre oblige à
 * repositionner l'image après chaque pincement, ce qui est exactement le geste
 * qu'on voulait éviter.
 *
 * `moved` sert au **garde du clic** : le navigateur émet un `click` après un
 * geste, et sans ce drapeau il effacerait la surimpression à chaque fois.
 */
function useStage(options: { readonly swipe: boolean; readonly zoom: boolean }): Stage {
  const [at, setAt] = useState({ scale: 1, x: 0, y: 0 });
  /** La valeur **courante**, lisible au milieu d'un geste sans attendre un rendu. */
  const now = useRef(at);
  now.current = at;
  const points = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<
    { dist: number; scale: number; x: number; y: number; ox: number; oy: number; cx: number; cy: number } | undefined
  >(undefined);
  /** L'origine du doigt unique — pour le balayage — et sa dernière position — pour le déplacement. */
  const origin = useRef<{ x: number; y: number } | undefined>(undefined);
  const last = useRef<{ x: number; y: number } | undefined>(undefined);
  /** Le geste a eu **deux doigts** : il ne peut plus se terminer en balayage. */
  const multi = useRef(false);
  const moved = useRef(false);

  const reset = (): void => {
    points.current.clear();
    pinch.current = undefined;
    origin.current = undefined;
    last.current = undefined;
    multi.current = false;
    now.current = { scale: 1, x: 0, y: 0 };
    setAt(now.current);
  };

  /**
   * Pose la transformation en la **bornant** : l'échelle entre 1 et 5, et le
   * déplacement à ce que l'agrandissement a fait sortir de la scène. Sans cette
   * seconde borne, on pousse l'image hors de l'écran et il n'existe aucun geste
   * pour la ramener.
   */
  const apply = (next: { scale: number; x: number; y: number }, box: DOMRect | undefined): void => {
    const scale = Math.min(MAX_SCALE, Math.max(1, next.scale));
    const limitX = box ? ((scale - 1) * box.width) / 2 : 0;
    const limitY = box ? ((scale - 1) * box.height) / 2 : 0;
    now.current = {
      scale,
      x: Math.min(limitX, Math.max(-limitX, next.x)),
      y: Math.min(limitY, Math.max(-limitY, next.y)),
    };
    setAt(now.current);
  };

  const style =
    at.scale === 1 && at.x === 0 && at.y === 0
      ? {}
      : { transform: `translate(${at.x}px, ${at.y}px) scale(${at.scale})` };

  return {
    style,
    moved,
    reset,
    handlers: {
      onPointerDown: (ev: PointerEvent) => {
        // La souris a les flèches, le clavier et les boutons ; lui donner le
        // glissement ferait changer d'image à chaque sélection de texte manquée.
        if (ev.pointerType === 'mouse') return;
        const host = ev.currentTarget as Element | null;
        try {
          host?.setPointerCapture?.(ev.pointerId);
        } catch {
          // Un pointeur déjà relâché refuse la capture : le geste vaut alors ce
          // qu'il vaut sans elle, ce n'est pas une raison de l'abandonner.
        }
        points.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
        if (points.current.size === 1) {
          origin.current = { x: ev.clientX, y: ev.clientY };
          last.current = { x: ev.clientX, y: ev.clientY };
          return;
        }
        if (points.current.size === 2 && options.zoom) {
          multi.current = true;
          const [a, b] = [...points.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
          const box = host?.getBoundingClientRect();
          const cx = (a.x + b.x) / 2;
          const cy = (a.y + b.y) / 2;
          pinch.current = {
            // Jamais zéro : deux doigts posés au même pixel diviseraient par lui.
            dist: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
            scale: now.current.scale,
            x: now.current.x,
            y: now.current.y,
            ox: box ? cx - (box.left + box.width / 2) : 0,
            oy: box ? cy - (box.top + box.height / 2) : 0,
            cx,
            cy,
          };
        }
      },
      onPointerMove: (ev: PointerEvent) => {
        const point = points.current.get(ev.pointerId);
        if (!point) return;
        point.x = ev.clientX;
        point.y = ev.clientY;
        const box = (ev.currentTarget as Element | null)?.getBoundingClientRect();

        const start = pinch.current;
        if (start && points.current.size >= 2) {
          const [a, b] = [...points.current.values()] as [{ x: number; y: number }, { x: number; y: number }];
          const ratio = Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)) / start.dist;
          const cx = (a.x + b.x) / 2;
          const cy = (a.y + b.y) / 2;
          moved.current = true;
          apply(
            {
              scale: start.scale * ratio,
              x: ratio * start.x + start.ox * (1 - ratio) + (cx - start.cx),
              y: ratio * start.y + start.oy * (1 - ratio) + (cy - start.cy),
            },
            box,
          );
          return;
        }

        // **Un doigt sur une image agrandie la déplace.** À l'échelle 1 il ne fait
        // rien pendant le geste : c'est au relâchement qu'on saura si c'était un
        // balayage, et bouger l'image d'aplomb n'aurait aucun sens.
        const from = last.current;
        if (points.current.size === 1 && now.current.scale > 1 && from) {
          last.current = { x: ev.clientX, y: ev.clientY };
          moved.current = true;
          apply(
            {
              scale: now.current.scale,
              x: now.current.x + (ev.clientX - from.x),
              y: now.current.y + (ev.clientY - from.y),
            },
            box,
          );
        }
      },
      onPointerUp: (ev: PointerEvent) => {
        points.current.delete(ev.pointerId);
        if (points.current.size < 2) pinch.current = undefined;
        // Le doigt qui reste devient la nouvelle référence du déplacement : sans
        // cela, l'image saute de l'écart entre les deux doigts au moment où l'on
        // en lève un.
        if (points.current.size === 1) {
          const [rest] = [...points.current.values()] as [{ x: number; y: number }];
          last.current = { x: rest.x, y: rest.y };
          return;
        }
        if (points.current.size > 0) return;

        const pinched = multi.current;
        const start = origin.current;
        multi.current = false;
        origin.current = undefined;
        last.current = undefined;

        // Presque revenu à l'échelle 1 : on l'y remet franchement, plutôt que de
        // laisser une image décalée de trois pixels sans moyen de la recadrer.
        if (now.current.scale <= SNAP_SCALE && (now.current.scale !== 1 || now.current.x !== 0 || now.current.y !== 0)) {
          apply({ scale: 1, x: 0, y: 0 }, undefined);
        }

        if (pinched || !options.swipe || now.current.scale > 1 || !start) return;
        const dx = ev.clientX - start.x;
        // Un glissement de moins de 40 px est un tremblement ; un glissement plus
        // vertical qu'horizontal est autre chose — les confondre ferait changer
        // d'image quand on veut fermer.
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(ev.clientY - start.y)) return;
        moved.current = true;
        // Glisser vers la gauche amène **la suivante** : c'est le sens du papier
        // qu'on pousse, et celui de toutes les galeries.
        step(dx < 0 ? 1 : -1);
      },
      onPointerCancel: (ev: PointerEvent) => {
        points.current.delete(ev.pointerId);
        if (points.current.size < 2) pinch.current = undefined;
        if (points.current.size === 0) {
          multi.current = false;
          origin.current = undefined;
          last.current = undefined;
        }
      },
    } as Record<string, (ev: never) => void>,
  };
}

/**
 * **Le plein écran du navigateur**, qui n'est pas celui de la surcouche.
 *
 * La visionneuse couvre déjà la fenêtre ; ce qu'elle ne peut pas reprendre sans
 * cette API, ce sont la barre d'adresse et les barres du système — sur un
 * téléphone en portrait, à peu près le quart de la hauteur, c'est-à-dire ce qui
 * manque justement à une photo. C'est le retour d'usage : « est-ce qu'il est
 * possible d'avoir un bouton full screen ? ».
 *
 * L'état est **relu du document** et non gardé en mémoire : on sort du plein écran
 * par la touche `Échap` du système ou par un geste de l'OS, sans que la page en
 * soit prévenue autrement que par `fullscreenchange`. Une mémoire locale y
 * dériverait, et le bouton finirait par annoncer le contraire de ce qui est.
 */
function useFullscreen(host: RefObject<HTMLElement>): {
  readonly on: boolean;
  readonly supported: boolean;
  readonly toggle: () => void;
  readonly leave: () => void;
} {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const sync = (): void => setOn(document.fullscreenElement !== null);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  const leave = (): void => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
  };

  return {
    on,
    // Safari sur iPhone n'a pas de plein écran d'élément : le bouton n'y est pas,
    // plutôt que d'y être et de ne rien faire.
    supported: Boolean(document.fullscreenEnabled),
    leave,
    toggle: () => {
      if (document.fullscreenElement) return leave();
      void host.current?.requestFullscreen?.().catch(() => undefined);
    },
  };
}

/** Tourne dans la liste. Boucler évite le cul-de-sac au dernier élément. */
function step(delta: number): void {
  const list = viewerList.value;
  const index = viewer.value;
  if (index === undefined || list.length === 0) return;
  viewer.value = (index + delta + list.length) % list.length;
}

/**
 * **Ce qu'on est en train de regarder** *(2026-08-15)*.
 *
 * La visionneuse a un repli — `standard` s'il existe, la `source` sinon — et rien
 * ne le disait : deux images identiques à l'œil pouvaient être l'une un dérivé de
 * 1 Mo, l'autre un original de 340 Mo tiré d'une archive. Le repli n'est pas rare :
 * une vidéo trop lourde n'a **jamais** de `standard` (M7), et c'est alors la source
 * qui se joue — sur un réseau cellulaire, ça se remarque à la facture avant de se
 * remarquer à l'écran.
 *
 * **Deux icônes plutôt que deux mots**, et la ligne du bas reste une ligne : un
 * écran pour la version d'écran, un carton d'archive pour l'original. Le mot n'est
 * pas perdu — il est dans le `title` et dans le nom accessible, donc atteignable
 * au survol comme au lecteur d'écran. La taille suit, parce que c'est l'argument
 * qui décide vraiment.
 *
 * L'information ne coûte **rien** : le rôle est déjà choisi ici, et sa taille est
 * dans `Variants` que la page a déjà.
 */
function Quality({ entity }: { readonly entity: Entity }): JSX.Element | null {
  const role = roleOf(entity);
  const size = entity.Variants?.[role]?.size;
  if (size === undefined) return null;

  const label = t(role === 'standard' ? 'inspector.role.standard' : 'inspector.role.source');
  return (
    // **La source s'écrit en rouge et en gras, le dérivé reste discret.** Les deux
    // ne disent pas la même chose : l'un est le cas nominal, l'autre est ce qui se
    // paie — en attente, en données, et en tarif de récupération d'archive. Un
    // indicateur qui traite les deux également ne serait qu'une décoration.
    <span
      class={role === 'source' ? 'viewer-quality archived' : 'viewer-quality'}
      data-testid={`viewer-quality-${role}`}
      title={label}
    >
      <span class="visually-hidden">{label}</span>
      {role === 'standard' ? <IconScreen size={14} /> : <IconOriginal size={14} />}
      {formatSize(size)}
    </span>
  );
}

/**
 * Deux rôles, dans cet ordre : `standard` s'il a des octets constatés, la
 * `source` sinon. Le drapeau est `Variants[role].size` — jamais `State` — parce
 * qu'une entité peut être `READY` pour sa source et n'avoir pas encore son
 * dérivé, et que demander l'URL d'un rôle sans octets rend `409`.
 *
 * **Une seule fonction**, parce que l'image et l'indicateur doivent nommer le
 * même rôle : deux lectures de la même règle finiraient par se contredire, et
 * l'indicateur mentirait — ce qui est pire que pas d'indicateur du tout.
 */
const roleOf = (entity: Entity): 'standard' | 'source' =>
  entity.Variants?.['standard']?.size !== undefined ? 'standard' : 'source';

/** L'image elle-même. */
function Frame({ entity }: { readonly entity: Entity }): JSX.Element {
  const fid = entity.Fid ?? '';
  const role = roleOf(entity);
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setSrc(undefined);
    setFailed(false);
    void urlFor(fid, role)
      .then((url) => {
        if (alive) setSrc(url);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [fid, role]);

  if (failed) {
    return (
      <p class="viewer-empty" data-testid="viewer-error">
        {t('viewer.unavailable')}
      </p>
    );
  }
  if (!src) {
    return (
      <p class="viewer-empty" data-testid="viewer-loading">
        <span class="spinner" aria-hidden="true" />
      </p>
    );
  }

  // **`crossOrigin` n'est pas décoratif** *(2026-08-18)*. Sans lui, le navigateur
  // demande ces octets **sans en-tête `Origin`** ; S3 répond donc **sans**
  // `Access-Control-Allow-Origin`, et met cette réponse en cache telle quelle.
  // Un `fetch()` ultérieur de la **même URL** — celui de « Envoyer » — est, lui,
  // une requête CORS : il retrouve l'entrée dépourvue de l'en-tête et échoue par
  // un `TypeError`, alors que l'image est sous les yeux.
  //
  // C'est exactement ce qui faisait échouer l'envoi d'une photo ou d'une vidéo
  // pendant que celui d'une note passait : rien ne précharge le corps d'une
  // note, donc son entrée de cache naît d'une requête CORS.
  //
  // En le posant ici, l'entrée devient utilisable **par les deux**, et l'envoi
  // reste un succès de cache — ce qui est toute la raison pour laquelle il n'est
  // proposé que sur un objet ouvert.
  if (entity.Kind === 'VID') {
    return (
      <video
        class="viewer-media"
        data-testid="viewer-video"
        src={src}
        crossOrigin="anonymous"
        controls
        autoPlay
      />
    );
  }
  return (
    <img
      class="viewer-media"
      data-testid="viewer-image"
      src={src}
      crossOrigin="anonymous"
      alt={entity.Name}
    />
  );
}
